/** Policy detail (spec §8.5): overview, renewal history, instalments, endorsements, claims, documents. */

import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Ban, CheckCheck, CheckCircle2, FileText, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { formatPolicyRef, isRenewalDue, today, type PolicyDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EnumBadge } from '@/components/common/enum-badge';
import { EmptyState, Field, FieldGrid } from '@/components/common/page-header';
import {
  DetailHeader,
  DetailMetrics,
  DetailPage,
  DetailPageSkeleton,
  DetailTabsBar,
  Metric,
  Panel,
  Section,
  SectionStack,
  Workspace,
} from '@/components/common/detail-page';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { PolicyRef } from '@/components/common/policy-ref';
import { DocumentPanel } from '@/components/common/document-panel';
import { RequiresConnection } from '@/components/offline/offline-indicator';
import {
  claimsApi,
  policiesApi,
  useCancellationPreview,
  useCancelPolicy,
  usePolicyEndorsements,
  usePolicyInstallments,
  usePolicyRenewals,
  useRecordInstallmentPayment,
} from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';

import { DaysBadge, policyStatusTone } from './list';
import { NoticeDialog } from '../notices';
import { MarkPaidDialog } from './mark-paid-dialog';
import { PolicyFormDialog } from './policy-form';
import { EndorsementFormDialog } from './endorsement-form';

export function PolicyDetailPage() {
  const { id = '' } = useParams();
  const { t, money, date, dateTime } = useI18n();
  const { can } = useAuth();

  const { data: policy, isLoading } = policiesApi.useDetail(id);
  const [payOpen, setPayOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const navigate = useNavigate();
  const removePolicy = policiesApi.useRemove({
    onSuccess: () => {
      toast.success(t('policy.deleted'));
      navigate(policy ? `/clients/${policy.clientId}` : '/policies');
    },
    onError: (error) => toast.error(error.message),
  });
  const [endorsementOpen, setEndorsementOpen] = React.useState(false);
  const [noticeOpen, setNoticeOpen] = React.useState(false);

  if (isLoading) return <DetailPageSkeleton />;
  if (!policy) return <EmptyState title={t('error.notFound')} hint={t('error.notFoundHint')} />;

  const canRenew = can('policy:renew') && isRenewalDue(policy);
  /*
   * A policy is «εξοφλημένο» from the moment it is renewed and stays that way for the whole
   * period, so the button sat beside a «Εξοφλημένο» badge offering to collect money nobody
   * owes. The dialog behind it is «Εξόφληση & ανανέωση» — when there is nothing to collect,
   * the half that remains is the renewal, and that is what the button now says.
   */
  const nothingToCollect = policy.paymentStatus === 'paid';

  return (
    <DetailPage>
      <DetailHeader
        breadcrumbs={[
          { label: t('policy.title'), to: '/policies' },
          { label: formatPolicyRef(policy.policyNumber, policy.plates) },
        ]}
        avatar={<EntityAvatar name={policy.insuranceCompanyName} size="lg" />}
        // The heading names the policy, so it leads with the plate. The «Αρ. συμβολαίου» field
        // further down keeps the bare number: a field that says what it holds must hold it.
        title={<PolicyRef policyNumber={policy.policyNumber} plates={policy.plates} />}
        status={
          <>
            <EnumBadge enum="policyStatus" value={policy.status} tone={policyStatusTone(policy)} />
            <EnumBadge enum="paymentStatus" value={policy.paymentStatus} />
          </>
        }
        meta={[
          { key: 'client', label: policy.clientName, to: `/clients/${policy.clientId}` },
          { key: 'company', label: policy.insuranceCompanyName },
          {
            key: 'type',
            label: <EnumBadge enum="insuranceType" value={policy.insuranceType} size="sm" />,
          },
        ]}
        actions={
          <>
            {canRenew && (
              <RequiresConnection reasonKey="offline.blocked.financial">
                <Button onClick={() => setPayOpen(true)}>
                  {nothingToCollect ? <RefreshCw /> : <CheckCircle2 />}{' '}
                  {t(nothingToCollect ? 'action.renewPeriod' : 'action.markPaid')}
                </Button>
              </RequiresConnection>
            )}
            {can('policy:write') && (
              <>
                <Button variant="outline" onClick={() => setEditOpen(true)}>
                  <Pencil /> {t('action.edit')}
                </Button>
                {/*
                  Two different acts, and the button used to call the first by the second's name:
                  «Διαγραφή» opened the ακυρωτικό, so deleting a duplicate cancelled it instead
                  and booked a refund nobody was owed (7390146642, 24/09/2026). Cancelling is a
                  real event with money attached; deleting is for an entry that should never
                  have existed.
                */}
                {policy.status !== 'cancelled' && (
                  <RequiresConnection reasonKey="offline.blocked.contract">
                    <Button variant="ghost" onClick={() => setCancelOpen(true)}>
                      <Ban /> {t('action.cancelPolicy')}
                    </Button>
                  </RequiresConnection>
                )}
              </>
            )}
            {can('policy:delete') && (
              <RequiresConnection reasonKey="offline.blocked.contract">
                <Button variant="ghost" className="text-destructive" onClick={() => setDeleteOpen(true)}>
                  <Trash2 /> {t('action.delete')}
                </Button>
              </RequiresConnection>
            )}
          </>
        }
      />

      {/*
        The figures an agent is asked for on the phone, in the order they are asked.
        «Περίοδος» was cut from this strip and is back, for a reason that only surfaced once
        the renewal-certificate work began: on a policy whose insurer reuses one central
        number, the term register is the ONLY way to say which renewal is being discussed.
        Those policies carry no per-period identifier at all, so «η 5η περίοδος» *is* the
        identifier — doing the job an ανανεωτήριο number does everywhere else.

        Hence the pairing rather than two separate metrics: the value names the term, and the
        hint carries the certificate number for the insurers that issue one. Both conventions
        are then read in the same place, and where there is no certificate the term stands
        alone — which is exactly the case that needed it.

        The frequency badge stays on the premium rather than returning here: 563,71 € is a
        different price per month than per six months, so it qualifies the amount, not the
        counter. It is the existing EnumBadge, so a new frequency value needs no screen
        edited. (Not «563,71 € / εξάμηνο»: the enum labels are adjectives — «Εξαμηνιαία» —
        and a hand-written per-period noun map would bypass the registry the app resolves
        every other label through.)
      */}
      <DetailMetrics>
        <Metric
          label={t('policy.premium')}
          value={money(policy.premiumAmount)}
          hint={<EnumBadge enum="frequency" value={policy.renewalFrequency} size="sm" />}
        />
        {/*
          The hint carries the BASE, not just the rate, and that is the point of the tile since
          the premium split landed: «17% επί 420,00 €» is checkable against the carrier's table,
          whereas a bare «17%» next to a gross premium invites the reader to do the multiplication
          in their head and get a different answer.

          With no καθαρά recorded the commission really is 0,00 € — the generated column multiplies
          the rate by COALESCE(net, 0) — so the tile says why in the hint and takes a warning tone.
          Reading 0,00 € with no explanation is what would make this look like a bug.
        */}
        <Metric
          label={t('policy.commission')}
          value={money(policy.commissionAmount)}
          tone={policy.netPremiumAmount ? undefined : 'warning'}
          hint={
            policy.netPremiumAmount
              ? `${policy.commissionPercentage}% · ${money(policy.netPremiumAmount)}`
              : t('policy.netPremiumMissing')
          }
        />
        <Metric
          label={t('policy.endDate')}
          value={date(policy.endDate)}
          hint={<DaysBadge days={policy.daysUntilExpiry} />}
        />
        <Metric
          label={t('policy.period')}
          value={policy.currentPeriodNumber ?? 1}
          hint={policy.renewalNumber ?? undefined}
        />
      </DetailMetrics>

      <Tabs defaultValue="overview">
        <DetailTabsBar>
          <TabsList variant="underline">
            <TabsTrigger value="overview">{t('common.details')}</TabsTrigger>
            <TabsTrigger value="renewals">{t('policy.renewalHistory')}</TabsTrigger>
            <TabsTrigger value="installments">{t('policy.installments')}</TabsTrigger>
            <TabsTrigger value="endorsements">{t('policy.endorsements')}</TabsTrigger>
            <TabsTrigger value="claims">{t('claim.title')}</TabsTrigger>
            <TabsTrigger value="documents">{t('common.documents')}</TabsTrigger>
          </TabsList>
        </DetailTabsBar>

        <TabsContent value="overview">
          <Workspace
            aside={
              policy.insuredItems && policy.insuredItems.length > 0 ? (
                <Section title={t('policy.insuredItems')}>
                  <div className="space-y-2.5">
                    {policy.insuredItems.map((item) => (
                      <div key={item.id} className="border-border rounded-xl border p-3.5">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-[13.5px] font-medium">{item.description}</span>
                          <EnumBadge enum="insuredItemType" value={item.itemType} size="sm" />
                        </div>
                        <div className="text-muted-soft mt-2 space-y-1 text-xs">
                          {item.plateNumber && (
                            <p>
                              {t('policy.plate')}:{' '}
                              <span className="text-foreground tabular font-medium">{item.plateNumber}</span>
                            </p>
                          )}
                          {item.year && (
                            <p>
                              {item.make} {item.model} · {item.year}
                            </p>
                          )}
                          {item.bmClass != null && (
                            <p>
                              {t('policy.bmClass')}:{' '}
                              <span className="text-foreground tabular font-medium">{item.bmClass}</span>
                            </p>
                          )}
                          {item.addressLine && <p>{item.addressLine}</p>}
                          {item.insuredValue && <p className="tabular">{money(item.insuredValue)}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : undefined
            }
          >
            <SectionStack>
              <Section title={t('section.identity')}>
                <FieldGrid>
                  <Field label={t('policy.number')} mono>
                    {policy.policyNumber}
                  </Field>
                  {/* Rendered only when the insurer issues one — an always-present «—» would
                      imply every policy should have a number and this one is missing it. */}
                  {policy.renewalNumber && (
                    <Field label={t('policy.renewalNumber')} mono>
                      {policy.renewalNumber}
                    </Field>
                  )}
                  <Field label={t('client.one')}>
                    <Link to={`/clients/${policy.clientId}`} className="text-primary hover:underline">
                      {policy.clientName}
                    </Link>
                  </Field>
                  <Field label={t('policy.company')}>{policy.insuranceCompanyName}</Field>
                  <Field label={t('policy.insuranceType')}>
                    <EnumBadge enum="insuranceType" value={policy.insuranceType} size="sm" />
                  </Field>
                  <Field label={t('client.agent')}>
                    {policy.assignedAgentName ?? t('common.unassigned')}
                  </Field>
                </FieldGrid>
              </Section>

              {/*
                These two sections carry only what the metrics strip does NOT already say.
                End date, frequency, premium and commission all live in the strip above;
                repeating them here meant reading the same four figures twice on one
                screen, which is most of what made this record feel scattered. The strip is
                the canonical place for them — it is what an agent reads on the phone.
              */}
              <Section
                title={t('section.term')}
                actions={
                  can('policy:write') && (
                    <Button variant="outline" size="sm" onClick={() => setNoticeOpen(true)}>
                      <CheckCheck /> {t('notices.record')}
                    </Button>
                  )
                }
              >
                <FieldGrid>
                  <Field label={t('policy.startDate')} mono>
                    {date(policy.startDate)}
                  </Field>
                  <Field label={t('policy.nextRenewal')} mono>
                    {date(policy.nextRenewalDate)}
                  </Field>
                  {/*
                    The ειδοποιητήριο lives in this section rather than its own: it is a fact
                    about the renewal, and separating it would imply it is a separate process
                    from the term it belongs to.
                  */}
                  <Field label={t('notices.status')}>
                    <EnumBadge enum="renewalNoticeStatus" value={policy.noticeStatus} size="sm" />
                  </Field>
                  {policy.noticeReceivedAt && (
                    <Field label={t('notices.received')} mono>
                      {date(policy.noticeReceivedAt)}
                    </Field>
                  )}
                  {policy.notifiedAt && (
                    <Field label={t('notices.notifiedAt')} mono>
                      {dateTime(policy.notifiedAt)}
                      {policy.notifiedByName ? (
                        <span className="text-muted-soft"> · {policy.notifiedByName}</span>
                      ) : null}
                    </Field>
                  )}
                  {policy.noticeNote && <Field label={t('notices.note')}>{policy.noticeNote}</Field>}
                </FieldGrid>
              </Section>

              {/*
                The strip above carries the ολικά; the καθαρά belong here rather than as a fifth
                tile. They are not a headline figure an agent reads out on the phone — they are
                the working number behind the commission, and they are checked when someone asks
                why a commission is what it is.
              */}
              <Section title={t('section.financial')}>
                <FieldGrid>
                  <Field label={t('policy.netPremium')} mono>
                    {policy.netPremiumAmount ? (
                      money(policy.netPremiumAmount)
                    ) : (
                      <span className="text-serious">{t('policy.netPremiumMissing')}</span>
                    )}
                  </Field>
                  {policy.vehicleCategory && (
                    <Field label={t('policy.vehicleCategory')}>
                      <EnumBadge enum="vehicleCategory" value={policy.vehicleCategory} size="sm" />
                    </Field>
                  )}
                  <Field label={t('commission.status')}>
                    <EnumBadge enum="commissionStatus" value={policy.commissionStatus} size="sm" />
                  </Field>
                </FieldGrid>
              </Section>

              {policy.coverageDescription && (
                <Section title={t('section.coverage')}>
                  <p className="text-[13.5px] leading-relaxed">{policy.coverageDescription}</p>
                </Section>
              )}

              {policy.notes && (
                <Section title={t('section.notes')}>
                  <p className="text-[13.5px] leading-relaxed whitespace-pre-line">{policy.notes}</p>
                </Section>
              )}

              {policy.cancellationReason && (
                <Section title={t('section.cancellation')}>
                  <FieldGrid>
                    <Field label={t('policy.cancelReason')}>
                      <span className="text-critical">{policy.cancellationReason}</span>
                    </Field>
                    {policy.cancellationRefund && (
                      <Field label={t('policy.refund')} mono>
                        <span className="text-good font-semibold">{money(policy.cancellationRefund)}</span>
                      </Field>
                    )}
                  </FieldGrid>
                </Section>
              )}
            </SectionStack>
          </Workspace>
        </TabsContent>

        <TabsContent value="renewals">
          <RenewalHistory policyId={id} />
        </TabsContent>

        <TabsContent value="installments">
          <InstallmentPanel policyId={id} />
        </TabsContent>

        <TabsContent value="endorsements">
          <Section
            title={t('policy.endorsements')}
            actions={
              can('policy:write') &&
              policy.status !== 'cancelled' && (
                <Button variant="outline" size="sm" onClick={() => setEndorsementOpen(true)}>
                  <Plus /> {t('endorsement.new')}
                </Button>
              )
            }
          >
            <EndorsementPanel policyId={id} />
          </Section>
        </TabsContent>

        <TabsContent value="claims">
          <PolicyClaims policyId={id} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentPanel scope="policies" ownerId={id} typeEnum="policyDocumentType" />
        </TabsContent>
      </Tabs>

      {payOpen && <MarkPaidDialog policy={policy} open onOpenChange={setPayOpen} />}
      <PolicyFormDialog open={editOpen} onOpenChange={setEditOpen} policy={policy} />
      <CancelDialog policy={policy} open={cancelOpen} onOpenChange={setCancelOpen} />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => !removePolicy.isPending && setDeleteOpen(open)}
        title={t('policy.deleteTitle')}
        subject={`${policy.clientName} · ${formatPolicyRef(policy.policyNumber, policy.plates)}`}
        description={t('policy.deleteHint')}
        onConfirm={() => removePolicy.mutate(policy.id)}
        loading={removePolicy.isPending}
      />
      <EndorsementFormDialog policy={policy} open={endorsementOpen} onOpenChange={setEndorsementOpen} />
      {noticeOpen && <NoticeDialog policy={policy} open onOpenChange={setNoticeOpen} />}
    </DetailPage>
  );
}

/** Spec §8.5: most recent period first. */
function RenewalHistory({ policyId }: { policyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = usePolicyRenewals(policyId);

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (!data?.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('policy.renewalHistory')} compact />
    );

  /*
   * The certificate column earns its width only for insurers that issue certificates. For a
   * policy that keeps one central number every cell would read «—», which is a column of
   * noise claiming something is missing. This is the table that answers "which ανανεωτήριο
   * covered these dates", so when the numbers exist they belong next to the dates.
   */
  const showRenewalNumber = data.some((renewal) => renewal.renewalNumber);

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('policy.period')}</TableHead>
            {showRenewalNumber && <TableHead>{t('policy.renewalNumber')}</TableHead>}
            <TableHead>{t('policy.startDate')}</TableHead>
            <TableHead>{t('policy.endDate')}</TableHead>
            <TableHead className="text-right">{t('policy.premium')}</TableHead>
            <TableHead className="text-right">{t('policy.commission')}</TableHead>
            <TableHead>{t('policy.dueDate')}</TableHead>
            <TableHead>{t('common.createdBy')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((renewal) => (
            <TableRow key={renewal.id}>
              <TableCell className="py-2.5 font-medium tabular">#{renewal.periodNumber}</TableCell>
              {showRenewalNumber && (
                <TableCell className="py-2.5 tabular">{renewal.renewalNumber ?? '—'}</TableCell>
              )}
              <TableCell className="py-2.5 tabular">{date(renewal.startDate)}</TableCell>
              <TableCell className="py-2.5 tabular">{date(renewal.endDate)}</TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(renewal.premiumAmount)}</TableCell>
              <TableCell className="text-muted-foreground py-2.5 text-right tabular">
                {money(renewal.commissionAmount)}
              </TableCell>
              <TableCell className="py-2.5 tabular">{date(renewal.paymentDate)}</TableCell>
              <TableCell className="text-muted-foreground py-2.5">{renewal.renewedByName ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function InstallmentPanel({ policyId }: { policyId: string }) {
  const { t, money, date } = useI18n();
  const { can } = useAuth();
  const { data, isLoading } = usePolicyInstallments(policyId);
  const recordPayment = useRecordInstallmentPayment(policyId);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.length)
    return (
      <EmptyState title={t('common.empty')} compact />
    );

  const pay = async (installmentId: string, amount: string) => {
    await recordPayment.mutateAsync({
      installmentId,
      paidAmount: amount,
      paymentDate: today(),
      paymentMethod: 'bank_transfer',
    });
    toast.success(t('common.saved'));
  };

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>{t('policy.dueDate')}</TableHead>
            <TableHead className="text-right">{t('policy.amount')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
            <TableHead>{t('policy.paymentMethod')}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((installment) => (
            <TableRow key={installment.id}>
              <TableCell className="py-2.5 tabular">
                {installment.installmentNumber}/{installment.totalInstallments}
              </TableCell>
              <TableCell className="py-2.5 tabular">{date(installment.dueDate)}</TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(installment.amount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="installmentStatus" value={installment.status} size="sm" />
              </TableCell>
              <TableCell className="text-muted-foreground py-2.5">
                <EnumBadge enum="paymentMethod" value={installment.paymentMethod} size="sm" variant="dot" />
              </TableCell>
              <TableCell className="py-2.5 text-right">
                {installment.status !== 'paid' && installment.status !== 'cancelled' && can('policy:write') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={recordPayment.isPending}
                    onClick={() => void pay(installment.id, installment.amount)}
                  >
                    {t('policy.recordPayment')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function EndorsementPanel({ policyId }: { policyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = usePolicyEndorsements(policyId);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('policy.endorsements')} compact />
    );

  // One panel of rows, not a stack of cards: these are entries in a ledger of amendments,
  // and eight elevated surfaces down the page reads as eight unrelated objects.
  return (
    <Panel className="divide-border divide-y">
      {data.map((endorsement) => {
        const delta = Number(endorsement.premiumDelta);
        return (
          <div
            key={endorsement.id}
            className="flex flex-wrap items-start justify-between gap-3 px-5 py-3.5"
          >
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <EnumBadge enum="endorsementType" value={endorsement.endorsementType} size="sm" />
                {endorsement.endorsementNumber && (
                  <span className="text-muted-soft tabular text-xs">{endorsement.endorsementNumber}</span>
                )}
                <span className="text-muted-soft tabular text-xs">{date(endorsement.effectiveDate)}</span>
              </div>
              <p className="mt-1.5 text-[13.5px]">{endorsement.description}</p>
            </div>
            <div className="text-right">
              <p className={cn('tabular text-[13.5px] font-semibold', delta < 0 && 'text-good')}>
                {delta >= 0 ? '+' : ''}
                {money(endorsement.premiumDelta)}
              </p>
              <p className="text-muted-soft tabular text-xs">
                {money(endorsement.previousPremiumAmount)} → {money(endorsement.newPremiumAmount)}
              </p>
            </div>
          </div>
        );
      })}
    </Panel>
  );
}

function PolicyClaims({ policyId }: { policyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = claimsApi.useList({ policyId, perPage: 50 });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.data.length)
    return (
      <EmptyState title={t('common.empty')} hint={t('claim.title')} compact />
    );

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('claim.number')}</TableHead>
            <TableHead>{t('claim.incidentDate')}</TableHead>
            <TableHead className="text-right">{t('claim.amount')}</TableHead>
            <TableHead className="text-right">{t('claim.payout')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((claim) => (
            <TableRow key={claim.id}>
              <TableCell className="py-2.5">
                <Link to={`/claims/${claim.id}`} className="text-primary font-medium hover:underline">
                  {claim.claimNumber ?? claim.id.slice(0, 8)}
                </Link>
              </TableCell>
              <TableCell className="py-2.5 tabular">{date(claim.incidentDate)}</TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(claim.claimAmount)}</TableCell>
              <TableCell className="py-2.5 text-right tabular">{money(claim.payoutAmount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="claimStatus" value={claim.status} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function CancelDialog({
  policy,
  open,
  onOpenChange,
}: {
  policy: PolicyDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, money } = useI18n();
  const [reason, setReason] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState(() => today());
  const cancel = useCancelPolicy();

  // Server-computed ακυρωτικό: the refund the operator sees here is exactly the one
  // that will be stored on the policy — same principle as the renewal preview.
  const preview = useCancellationPreview(policy.id, effectiveDate, open);
  const p = preview.data;

  const submit = async () => {
    await cancel.mutateAsync({ policyId: policy.id, reason, effectiveDate });
    toast.success(t('common.saved'));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t('policy.cancelTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cancel-date">{t('policy.effectiveDate')}</Label>
            <Input
              id="cancel-date"
              type="date"
              value={effectiveDate}
              onChange={(event) => setEffectiveDate(event.target.value)}
            />
          </div>

          {p && (
            <div className="bg-muted/50 space-y-1.5 rounded-lg border p-3 text-[13px]">
              <p className="text-muted-foreground text-xs font-medium">{t('policy.cancelPreview')}</p>
              <p className="text-muted-foreground text-xs">
                {t('policy.periodUsed', { used: p.usedDays, total: p.totalDays })}
              </p>
              <div className="flex justify-between">
                <span>{t('policy.earnedPremium')}</span>
                <span className="tabular">{money(p.earnedPremium)}</span>
              </div>
              {p.premiumPaid ? (
                <>
                  <div className="flex justify-between font-medium">
                    <span>{t('policy.refundDue')}</span>
                    <span className="text-good tabular">{money(p.unearnedPremium)}</span>
                  </div>
                  <div className="text-muted-foreground flex justify-between text-xs">
                    <span>{t('policy.commissionClawback')}</span>
                    <span className="tabular">{money(p.commissionClawback)}</span>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-xs">{t('policy.refundNotPaid')}</p>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cancel-reason">{t('policy.cancelReason')}</Label>
            <Input
              id="cancel-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              autoFocus
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length < 3}
            loading={cancel.isPending}
            onClick={() => void submit()}
          >
            {t('action.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { FileText, Plus, Badge };
